import { useAtom } from "@dbeining/react-atom";
import { Form as FormikForm, Formik } from "formik";
import { useEffect, useState } from "react";
import { Alert, Container } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { Link, useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { userAtom } from "../../atoms";
import { LocationMapAddressFields } from "../../components/features/location/LocationMapAddressFields";
import { ConfirmationModel } from "../../components/features/common";
import { LoadingSpinner } from "../../components/common/Loading/LoadingSpinner";
import { EditFormSaveCancelButtons, EditFormSplitFooter } from "../../components/Forms";
import { ResponsiveBreadcrumb } from "../../components/Layout/ResponsiveBreadcrumb";
import type { ISiteOption, CustomerCreatePayload } from "../../customTypings/customer/Customer";
import { useGoogleMapsConfig } from "../../hooks/useGoogleMapsConfig";
import { useCustomerRestoreOnCreateConflict } from "../../hooks/useCustomerRestoreOnCreateConflict";
import { sitesService } from "../../services/sites/sites.service";
import { customersService } from "../../services/customers/customers.service";
import {
    createEditFormSubmitHandler,
    DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE,
    optionalNullableString,
    editFormBreadcrumbLabel,
    editPageTitle,
    EntityLabels,
    hydratePostalAddressForForm,
    ListPageTitles,
    loadingPageTitle,
    mapPostalAddressToApi,
    type PostalAddressFormFields,
    SectionLabels,
    showToastError,
    showToastSuccess,
    getUserFacingErrorMessage,
    logErrorForDev,
} from "../../utils";
import { canMutateCustomers, formatCustomerToastLabel } from "../CustomersListPage/customerAdminListActions";
import { CustomerEditFormGrid } from "./CustomerEditFormGrid";

const SITE_STORAGE_KEY = "CustomersListPage.siteId";

type CustomerEditLocationState = { siteId?: string };

type CustomerCreateFormValues = {
    name: string;
    isEnabled: boolean;
    latitude: number | null;
    longitude: number | null;
} & PostalAddressFormFields;

const coordinateField = Yup.number()
    .transform((_value, originalValue) => {
        if (originalValue === "" || originalValue === null || originalValue === undefined) {
            return undefined;
        }
        return Number(originalValue);
    })
    .typeError("Please pick a point on the map or search for an address.")
    .required("Please pick a point on the map or search for an address.");

const validationSchema = Yup.object({
    name: Yup.string().trim().required("Name is required.").max(255, "Name must be at most 255 characters."),
    isEnabled: Yup.boolean().required(),
    addressLine1: optionalNullableString,
    addressLine2: optionalNullableString,
    addressLine3: optionalNullableString,
    town: optionalNullableString,
    county: optionalNullableString,
    postcode: optionalNullableString,
    country: optionalNullableString,
    latitude: coordinateField,
    longitude: coordinateField,
});

const emptyForm: CustomerCreateFormValues = {
    name: "",
    isEnabled: true,
    ...hydratePostalAddressForForm({}),
    latitude: null,
    longitude: null,
};

/** Create-only page at `/customers/new`. Edit existing customers via {@link CustomerEditLayout}. */
const CustomerEditPage = () => {
    const location = useLocation();
    const navigationSiteId = (location.state as CustomerEditLocationState | null)?.siteId ?? "";
    const navigate = useNavigate();
    const currentUser = useAtom(userAtom);
    const roleNames = currentUser?.identity.roles.map((r) => r.name) ?? [];
    const canMutate = canMutateCustomers(roleNames);
    const { config: mapConfig, loading: mapConfigLoading, error: mapConfigError } = useGoogleMapsConfig();
    const mapsEnabled =
        mapConfig?.enabled === true && Boolean(mapConfig.browserApiKey) && Boolean(mapConfig.mapId);

    const [siteId, setSiteId] = useState(navigationSiteId);
    const [sites, setSites] = useState<ISiteOption[]>([]);
    const showSiteField = sites.length > 1;
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [initialValues] = useState<CustomerCreateFormValues>(emptyForm);
    const [liveValidationAfterSubmit, setLiveValidationAfterSubmit] = useState(false);

    const restoreOnCreate = useCustomerRestoreOnCreateConflict({
        restoreDeletedWithCreateValues: (payload) => {
            if (!siteId) return Promise.reject("Site is required.");
            return customersService.restoreDeletedWithCreateValues(siteId, payload);
        },
        onRestored: (detail) =>
            navigate(`/customers/${detail.id}`, { replace: true, state: { siteId: detail.siteId } }),
    });

    useEffect(() => {
        setLoading(true);
        sitesService
            .listForOrganisation()
            .then((list) => {
                setSites(list);
                if (list.length === 1) {
                    setSiteId(list[0].id);
                } else if (navigationSiteId && list.some((s) => s.id === navigationSiteId)) {
                    setSiteId(navigationSiteId);
                }
                setLoadError(null);
            })
            .catch((e: unknown) => {
                logErrorForDev("CustomerEditPage.sites", e);
                setLoadError(getUserFacingErrorMessage(e, "Could not load sites."));
            })
            .finally(() => setLoading(false));
    }, [navigationSiteId]);

    if (!canMutate) {
        return (
            <Container fluid className="my-3">
                <Alert variant="warning">You do not have permission to manage customers.</Alert>
            </Container>
        );
    }

    if (loading || mapConfigLoading) {
        return (
            <Container fluid className="my-5 d-flex justify-content-center">
                <LoadingSpinner text={loadingPageTitle(EntityLabels.customer)} />
            </Container>
        );
    }

    if (loadError) {
        return (
            <Container fluid className="my-3">
                <Alert variant="danger">{loadError}</Alert>
                <Link to="/customers">Back to customers</Link>
            </Container>
        );
    }

    return (
        <Container fluid>
            <Helmet>
                <title>{editPageTitle(EntityLabels.customer, true)}</title>
            </Helmet>
            <ResponsiveBreadcrumb
                className="mb-3"
                items={[
                    { text: SectionLabels.operations, to: "/operations" },
                    { text: ListPageTitles.customers, to: "/customers" },
                    { text: editFormBreadcrumbLabel(true) },
                ]}
            />

            <div
                className="d-flex align-items-center justify-content-between gap-2 mb-3 w-100"
                style={{ borderBottom: "2px solid var(--primary)", paddingBottom: "0.5rem" }}
            >
                <h1 className="mb-0 flex-grow-1 min-w-0" style={{ borderBottom: "none", paddingBottom: 0 }}>
                    {editPageTitle(EntityLabels.customer, true)}
                </h1>
            </div>

            {mapConfigError && (
                <Alert variant="danger" className="mb-3">
                    {mapConfigError}
                </Alert>
            )}

            {!mapsEnabled && (
                <Alert variant="warning" className="mb-3">
                    Maps are not set up for this system. You cannot use the map or address search until an administrator enables maps.
                    {" "}You cannot create a new customer until maps are enabled and coordinates can be set.
                </Alert>
            )}

            <Formik<CustomerCreateFormValues>
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnMount={false}
                validateOnChange
                validateOnBlur
                onSubmit={async (values) => {
                    if (!siteId) {
                        showToastError("Site is required.");
                        return;
                    }
                    if (!mapsEnabled) {
                        showToastError("Maps must be turned on before you can create a customer with a primary address on the map.");
                        return;
                    }
                    setLiveValidationAfterSubmit(true);
                    const base: CustomerCreatePayload = {
                        name: values.name.trim(),
                        isEnabled: values.isEnabled,
                        initialMainLocation: {
                            ...mapPostalAddressToApi(values),
                            latitude: values.latitude!,
                            longitude: values.longitude!,
                            isEnabled: true,
                        },
                    };
                    restoreOnCreate.setPendingRestoreForm(base);
                    try {
                        const created = await customersService.create(siteId, base);
                        setLiveValidationAfterSubmit(false);
                        showToastSuccess(`${formatCustomerToastLabel(base.name)} created successfully!`);
                        navigate(`/customers/${created.id}`, { replace: true, state: { siteId } });
                    } catch (err: unknown) {
                        if (restoreOnCreate.handleCreateError(err)) return;
                        showToastError(typeof err === "string" ? err : DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
                    }
                }}
            >
                {({ errors, touched, isSubmitting, handleSubmit, validateForm, setTouched, values, submitCount }) => {
                    const createSaveBlocked = !mapsEnabled;

                    return (
                        <FormikForm
                            noValidate
                            onSubmit={createEditFormSubmitHandler(validateForm, setTouched, values, handleSubmit)}
                        >
                            <CustomerEditFormGrid
                                showSiteField={showSiteField}
                                isNew
                                metaDeleted={false}
                                siteId={siteId}
                                setSiteId={setSiteId}
                                siteDisplayName=""
                                sites={sites}
                                liveValidationAfterSubmit={liveValidationAfterSubmit}
                                errors={errors}
                                touched={touched}
                            />

                            <div className="mt-4 pt-3">
                                <LocationMapAddressFields
                                    mapConfig={mapConfig}
                                    mapsEnabled={mapsEnabled}
                                    metaDeleted={false}
                                    showNameField={false}
                                    showLocationEnabledField={false}
                                    isNew
                                    liveValidationAfterSubmit={liveValidationAfterSubmit}
                                    submitCount={submitCount}
                                    nameAutoFocus={false}
                                />
                            </div>

                            <EditFormSplitFooter>
                                <EditFormSaveCancelButtons
                                    isSubmitting={isSubmitting}
                                    disabled={createSaveBlocked}
                                    saveLabel="Create"
                                    onCancel={() => navigate("/customers")}
                                />
                            </EditFormSplitFooter>
                        </FormikForm>
                    );
                }}
            </Formik>

            <ConfirmationModel
                show={restoreOnCreate.restoreModalProps.show}
                onClose={(confirmed) => void restoreOnCreate.confirmRestore(confirmed)}
                title={restoreOnCreate.restoreModalProps.title}
                body={restoreOnCreate.restoreModalProps.body}
                buttonSuccessText={restoreOnCreate.restoreModalProps.buttonSuccessText}
                buttonCancelText={restoreOnCreate.restoreModalProps.buttonCancelText}
            />
        </Container>
    );
};

export { CustomerEditPage };
